import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useDraft from "./hooks/useDraft";
import CommonTable, { HeadCell } from "src/business-types/grocery/components/CommonTable";
import { IconButton, Tooltip } from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import { useUserTheme } from "src/shared/hooks/useUserTheme";
import CustomDropdownSelect from "src/business-types/grocery/components/DropdownSelect";

interface DraftTableRow {
  id: string;
  serialNumber: number;
  name: string;
  phone: string;
  status: string;
  action: React.ReactNode;
}

interface DraftTableProps {
  statusFilter?: string;
  onStatusFilterChange?: (value: string) => void;
}

export const STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "Accepted", label: "Accepted" },
  { value: "Rejected", label: "Rejected" },
];

function DraftTable({ statusFilter = "Pending", onStatusFilterChange }: DraftTableProps) {
  const { sidebarThemeClass, fontClass, borderHighlight } = useUserTheme();
  const navigate = useNavigate();

  const [localStatusFilter, setLocalStatusFilter] = useState(statusFilter || "Pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { draftTable } = useDraft(undefined, localStatusFilter || undefined, debouncedSearch || undefined);

  useEffect(() => {
    setLocalStatusFilter(statusFilter);
  }, [statusFilter]);

  const handleViewOrder = (draftId: string) => {
    navigate(`/v1/draft-details/${draftId}`);
  };

  const handleStatusChange = (value: string) => {
    setLocalStatusFilter(value);
    onStatusFilterChange?.(value);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const transformedDrafts: DraftTableRow[] = draftTable.map((order, index) => ({
    id: order._id,
    serialNumber: index + 1,
    name: [order.customerDetails.firstName, order.customerDetails.middleName, order.customerDetails.lastName].filter(Boolean).join(" ") || "N/A",
    phone: order.customerDetails.phones.find((phone) => phone.isPrimary)?.phone || "N/A",
    status: order.approvalStatus,
    action: (
      <Tooltip title="View order">
        <IconButton onClick={() => handleViewOrder(order._id)}>
          <ViewListIcon sx={{ color: "#000" }} />
        </IconButton>
      </Tooltip>
    ),
  }));

  const headCells: HeadCell<DraftTableRow>[] = [
    { id: "serialNumber", label: "S.No", sorting: false },
    { id: "name", label: "Name" },
    { id: "phone", label: "Phone number" },
    { id: "status", label: "Approval status" },
    { id: "action", label: "Action" },
  ];

  return (
    <div className="flex flex-col h-full w-full overflow-hidden p-1">
      <div className={`w-full h-full flex flex-col shadow-inner rounded-lg overflow-auto ${sidebarThemeClass}`}>
        <div className="w-full overflow-y-auto p-2">
          <div className="flex flex-wrap items-end justify-between gap-4 px-3 pb-2">
            {/* Status Filter */}
            <div className="w-48 min-w-0">
              <CustomDropdownSelect
                title="Status Filter"
                titleClassName="font-bold"
                name="statusSelected"
                value={localStatusFilter}
                // titleofSelect="Pending"
                options={STATUS_OPTIONS.map((o) => ({
                  _id: o.value,
                  CategoryName: o.label,
                }))}
                onChange={handleStatusChange}
                className="!mb-0 w-full"
              />
            </div>

            {/* Search — add an invisible label to match the dropdown's label height */}
            <div className={`flex-1 min-w-[220px] max-w-md ${fontClass}`}>
              <label className="block font-bold text-sm mb-1 invisible" aria-hidden="true">
                &nbsp;
              </label>
              <input
                type="text"
                name="order-search"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search Phone, Name"
                className={`w-full px-3 border rounded-md focus:outline-none focus:ring-2 ${borderHighlight} ${fontClass} placeholder-gray-400 text-sm text-black bg-white h-10`}
              />
            </div>
          </div>

          <CommonTable<DraftTableRow>
            data={transformedDrafts}
            headCells={headCells}
            title="Draft orders"
            showToolbar={false}
            selectable={false}
            denseToggle={false}
            className="w-full shadow-inner rounded-lg bg-white p-1.5 mt-2"
          />
        </div>
      </div>
    </div>
  );
}

export default DraftTable;
